import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import { Card, Btn, Field, Input, Select, Th, Badge, KPI, fmtN } from "./camCommon";

export default function CamReconciliation() {
  const [recon, setRecon] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [moves, setMoves] = useState([]);
  const [form, setForm] = useState({ movement_no: "", volume: "", ticket_date: "", source: "terminal" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/cam/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
    api.get("/cam/tickets").then((r) => setTickets(r.data || [])).catch(() => {});
    api.get("/cam/movements").then((r) => setMoves(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cam/tickets", form);
      toast.success("Ticket recorded");
      setForm({ movement_no: "", volume: "", ticket_date: "", source: "terminal" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removeTicket = async (t) => {
    if (!window.confirm(`Delete ticket ${t.ticket_no}?`)) return;
    try {
      await api.delete(`/cam/tickets/${t.id}`);
      toast.success("Ticket deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!recon) return <div className="p-4 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="cam-reconciliation">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Matched" value={fmtN(recon.matched)} tone="text-[#1F6B4A]" testid="recon-kpi-matched" />
        <KPI label="Exceptions" value={fmtN(recon.exceptions)} tone={recon.exceptions ? "text-red-600" : "text-[#0B1E3F]"} testid="recon-kpi-exceptions" />
        <KPI label="Missing Tickets" value={fmtN(recon.missing_tickets)} tone={recon.missing_tickets ? "text-amber-600" : "text-[#0B1E3F]"} testid="recon-kpi-missing" />
        <KPI label="Orphan Tickets" value={fmtN((recon.orphan_tickets || []).length)} testid="recon-kpi-orphans" />
      </div>

      <Card title="Movement ↔ Ticket Reconciliation" subtitle="Delivered volumes vs terminal/carrier tickets — variance beyond 0.5% tolerance flags an exception" testid="cam-recon-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Movement</Th><Th>Deal</Th><Th>Commodity</Th><Th>Mode</Th>
                <Th className="text-right">Actual Vol</Th><Th className="text-right">Ticketed Vol</Th>
                <Th className="text-right">Variance</Th><Th>Tickets</Th><Th>Match</Th>
              </tr>
            </thead>
            <tbody>
              {(recon.rows || []).map((r) => (
                <tr key={r.movement_no} className="border-b border-slate-100 hover:bg-slate-50/60"
                  data-testid={`recon-row-${r.movement_no}`}>
                  <td className="py-2 pr-3 font-mono text-xs font-semibold text-[#0B1E3F]">{r.movement_no}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.deal_no}</td>
                  <td className="py-2 pr-3 font-semibold">{r.commodity}</td>
                  <td className="py-2 pr-3 text-xs">{r.mode}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(r.actual_volume)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(r.ticketed_volume)}</td>
                  <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${Math.abs(r.variance) > 1 ? "text-red-600" : "text-slate-500"}`}>
                    {r.variance > 0 ? "+" : ""}{fmtN(r.variance, 2)}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-slate-500">{(r.tickets || []).join(", ") || "—"}</td>
                  <td className="py-2"><Badge v={r.match_status} testid={`recon-status-${r.movement_no}`} /></td>
                </tr>
              ))}
              {(recon.rows || []).length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-400 text-xs" data-testid="recon-empty">
                  No delivered movements to reconcile yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {(recon.orphan_tickets || []).length > 0 && (
          <div className="mt-4 border border-amber-200 bg-amber-50/50 rounded-sm p-3" data-testid="recon-orphans">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-700 mb-1.5">Orphan tickets (movement not delivered)</div>
            {recon.orphan_tickets.map((t) => (
              <div key={t.ticket_no} className="text-xs text-slate-600 font-mono">
                {t.ticket_no} → {t.movement_no} · {fmtN(t.volume)}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Tickets" subtitle="Record terminal & carrier tickets against movements — the back-office source of truth"
        testid="cam-tickets-card">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end mb-5">
          <Field label="Movement *">
            <Select value={form.movement_no} onChange={set("movement_no")} data-testid="ticket-movement">
              <option value="">Select movement…</option>
              {moves.map((m) => (
                <option key={m.movement_no} value={m.movement_no}>
                  {m.movement_no} — {m.commodity} {m.from_location}→{m.to_location}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Volume *">
            <Input type="number" min="0" value={form.volume} onChange={set("volume")} data-testid="ticket-volume" />
          </Field>
          <Field label="Ticket Date *">
            <Input type="date" value={form.ticket_date} onChange={set("ticket_date")} data-testid="ticket-date" />
          </Field>
          <Field label="Source">
            <Select value={form.source} onChange={set("source")} data-testid="ticket-source">
              {["terminal", "carrier", "inspector"].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Btn onClick={save} disabled={saving} data-testid="ticket-save-btn" className="h-[34px]">
            {saving ? "Saving…" : "Record Ticket"}
          </Btn>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <Th>Ticket</Th><Th>Movement</Th><Th className="text-right">Volume</Th>
              <Th>Date</Th><Th>Source</Th><Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`ticket-row-${t.ticket_no}`}>
                <td className="py-2 pr-3 font-mono text-xs font-semibold text-[#0B1E3F]">{t.ticket_no}</td>
                <td className="py-2 pr-3 font-mono text-xs">{t.movement_no}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtN(t.volume)}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{t.ticket_date}</td>
                <td className="py-2 pr-3 text-xs">{t.source}</td>
                <td className="py-2">
                  <button onClick={() => removeTicket(t)} data-testid={`ticket-delete-${t.ticket_no}`}
                    className="text-slate-400 hover:text-red-600" title="Delete"><Trash size={15} /></button>
                </td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-xs" data-testid="tickets-empty">
                No tickets recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
